import { type Media, type ProductTypes, Store, StoreNames } from "~/types";
import { ProductNames } from "~/types";
import type { ApiErrorResponse } from "~/types";
import { find, first, flatten, includes, isArray, map } from "lodash-es";
import type { Collection, Image } from "~/types";
import moment from "moment/moment";

export function getProductName(productType: ProductTypes): string {
  return ProductNames[productType];
}

export function getStoreName(store: Store): string {
  if (!store) return "-";
  return StoreNames[store];
}

export function handleApiError(e: any) {
  const { $toast } = useNuxtApp();
  const apiError = e.response._data as ApiErrorResponse;
  $toast.add({
    severity: "error",
    summary: first(map(apiError.errors, "title")),
    detail: first(map(apiError.errors, "detail")),
    life: 3000,
  });
}

export function getCollectionLink(_options: {
  type: Collection["type"];
  slug: string;
}) {
  const { type, slug } = _options;
  const abstractEntityType = getAbstractEntityType(type);
  return abstractEntityType === "video"
    ? `/videos/${slug}`
    : `/collections/${slug}`;
}

export function getThumbnailImage(
  images: Image[][] | Image[] | Image | Media | undefined,
) {
  if (!images) {
    return undefined;
  }
  if (isArray(images)) {
    const imagesArray = flatten(images);
    return find(imagesArray, ["label", "SD"])?.url;
  }
  return images.url;
}

export function parseMobileNumber(phoneNumber: string, dialCode: string) {
  let result = "";
  if (!phoneNumber) {
    return result;
  } else if (phoneNumber.startsWith("+")) {
    result = phoneNumber;
  } else {
    result = `+${dialCode} ${phoneNumber}`;
  }
  return result;
}

export function getDuration(duration: number) {
  return `${Math.round(duration / 60)} min`;
}

export function getAbstractEntityType(type: string) {
  if (includes(["collection", "playlist"], type)) {
    return "collection";
  } else {
    return "video";
  }
}

export function isCollection(type: string) {
  return getAbstractEntityType(type) === "collection";
}

export function getDeviceType() {
  const ua = navigator.userAgent.toLowerCase();
  const isTablet = /tablet|ipad|playbook|silk/i.test(ua);
  const isMobile = /mobile|android|iphone|ipod/i.test(ua);

  if (isTablet) {
    return "tablet";
  } else if (isMobile) {
    return "mobile";
  } else {
    return "desktop";
  }
}

export function getCollectionTimestamp(
  collection: Omit<Collection, "collection_video">,
) {
  let result = "";
  if (isCollection(collection.type)) {
    if (collection?.collection_tag?.show_start_date) {
      result += moment(collection.collection_tag.show_start_date).format(
        "MMM DD, YYYY",
      );
    }
    if (collection?.collection_tag?.show_end_date) {
      result += `- ${moment(collection.collection_tag.show_end_date).format("MMM DD, YYYY")}`;
    }
  } else {
    result += getFormattedTimestamp(collection?.aired_at);
  }
  return result;
}

export function getFormattedTimestamp(
  time: string,
  format = "MMM DD, hh:mm A",
) {
  return moment(time).format(format);
}
